(() => {
  if (customElements.get('product-block-swatches')) return;

  const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

  class ProductBlockSwatches extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onPointerOver = this.onPointerOver.bind(this);
      this.onMediaLeave = this.onMediaLeave.bind(this);
      this.onImageEnter = this.onImageEnter.bind(this);
      this.onImageLeave = this.onImageLeave.bind(this);
      this.onListLeave = this.onListLeave.bind(this);
      this.onWarmUp = this.onWarmUp.bind(this);
    }

    connectedCallback() {
      this.block = this.closest('[data-product-id]');
      this.media = this.closest('[data-product-media]') || this.block;
      this.previewOnly = this.hasAttribute('data-preview-only');
      this.hoverPreview = this.hasAttribute('data-hover-preview') || this.previewOnly;

      const productLink = this.block?.querySelector('[data-product-link]');
      this.defaultUrl = productLink?.getAttribute('href');
      this.defaultLabel = productLink?.getAttribute('aria-label');

      this.ownLink = this.querySelector('[data-swatch-link]');
      this.initialLink = this.previewOnly ? null : this.querySelector('[data-swatch-item].current [data-swatch-link]');
      this.activeLink = this.initialLink;
      this.activeHighlight = true;
      this.selectedLink = this.initialLink;

      const image = this.block?.querySelector('[data-product-media]');
      this.sliderImage = image?.querySelector('[data-slider-track]') ? image : null;
      this.list = this.querySelector('[data-swatch-list]');

      this.setEventListeners();
    }

    disconnectedCallback() {
      this.removeEventListeners();
    }

    setEventListeners() {
      this.block?.addEventListener('pointerenter', this.onWarmUp);
      this.addEventListener('touchstart', this.onWarmUp, { passive: true });
      this.addEventListener('click', this.onClick);
      this.addEventListener('pointerover', this.onPointerOver);
      if (this.previewOnly) this.list?.addEventListener('pointerleave', this.onListLeave);
      this.media?.addEventListener('pointerleave', this.onMediaLeave);
      this.sliderImage?.addEventListener('pointerenter', this.onImageEnter);
      this.sliderImage?.addEventListener('pointerleave', this.onImageLeave);
    }

    removeEventListeners() {
      this.block?.removeEventListener('pointerenter', this.onWarmUp);
      this.removeEventListener('touchstart', this.onWarmUp);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('pointerover', this.onPointerOver);
      this.list?.removeEventListener('pointerleave', this.onListLeave);
      this.media?.removeEventListener('pointerleave', this.onMediaLeave);
      this.sliderImage?.removeEventListener('pointerenter', this.onImageEnter);
      this.sliderImage?.removeEventListener('pointerleave', this.onImageLeave);
    }

    onClick(evt) {
      const link = evt.target.closest('[data-swatch-link]');
      if (!link) return;

      if (this.isDisabled(link)) {
        evt.preventDefault();
        return;
      }

      if (this.hoverPreview && hoverQuery.matches) return;

      evt.preventDefault();
      this.setActiveSwatch(link);
      this.selectedLink = link;
    }

    onPointerOver(evt) {
      if (!this.hoverPreview || evt.pointerType !== 'mouse') return;

      const link = evt.target.closest('[data-swatch-link]');
      if (!link || this.isDisabled(link)) return;

      this.setActiveSwatch(link);
    }

    onListLeave(evt) {
      if (evt.pointerType !== 'mouse') return;
      this.setActiveSwatch(this.selectedLink);
    }

    onMediaLeave(evt) {
      if (!this.hoverPreview || evt.pointerType !== 'mouse') return;
      this.setActiveSwatch(this.initialLink);
    }

    onImageEnter(evt) {
      if (evt.pointerType !== 'mouse') return;
      this.setActiveSwatch(this.ownLink, !this.previewOnly);
    }

    onImageLeave(evt) {
      if (evt.pointerType !== 'mouse') return;
      this.setActiveSwatch(this.selectedLink);
    }

    onWarmUp() {
      this.block?.removeEventListener('pointerenter', this.onWarmUp);
      this.removeEventListener('touchstart', this.onWarmUp);
      this.block?.querySelectorAll('[data-swatch-image]').forEach((el) => this.loadImages(el));
    }

    loadImages(container) {
      container.querySelectorAll('.lazyload--manual').forEach((imgEl) => {
        imgEl.classList.remove('lazyload--manual');
        imgEl.classList.add('lazyload');
      });
    }

    isDisabled(link) {
      return link.getAttribute('aria-disabled') === 'true' || link.closest('[data-swatch-item]')?.classList.contains('out-of-stock');
    }

    setActiveSwatch(link, highlight = true) {
      if (!this.block || (link === this.activeLink && highlight === this.activeHighlight)) return;

      const productUrl = link ? link.getAttribute('data-product-url') : this.defaultUrl;
      if (!productUrl) return;

      this.activeLink = link;
      this.activeHighlight = highlight;

      const label = link ? link.getAttribute('title') : this.defaultLabel;
      this.block.querySelectorAll('[data-product-link]').forEach((el) => {
        el.setAttribute('href', productUrl);
        if (label) {
          el.setAttribute('aria-label', label);
        }
      });

      this.block.querySelectorAll('[data-swatch-image]').forEach((el) => {
        const isActive = link ? el.getAttribute('data-product-url') === productUrl : el.hasAttribute('data-swatch-default');
        el.classList.toggle('product-block__image--active', isActive);
        if (isActive) {
          el.removeAttribute('aria-hidden');
        } else {
          el.setAttribute('aria-hidden', 'true');
        }

        if (isActive) this.loadImages(el);
      });

      let highlightLink = highlight ? link : null;
      if (!highlightLink && this.previewOnly) highlightLink = this.ownLink;

      this.querySelectorAll('[data-swatch-item]').forEach((el) => {
        el.classList.toggle('current', Boolean(highlightLink) && el.contains(highlightLink));
      });

      this.block.dispatchEvent(new CustomEvent('product-block:swatch-change'));
    }
  }

  customElements.define('product-block-swatches', ProductBlockSwatches);
})();
